import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import {
  AI_ANSWER_LANGUAGE_OPTIONS,
  answerLanguageLabel,
  defaultAnswerLanguage,
  normalizeAnswerLanguage,
  type AiAnswerLanguage,
} from "../ai-answer-language.ts";
import { t, type UiLocale } from "../i18n.ts";

@customElement("ead-ai-answer-language-field")
export class EadAiAnswerLanguageField extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .field-row {
      display: flex;
      align-items: stretch;
      gap: 6px;
      max-width: 320px;
    }

    .field-input {
      flex: 1 1 auto;
      min-width: 0;
      padding: 6px 10px;
      border-radius: 8px;
      border: 1px solid color-mix(in srgb, var(--border) 76%, transparent);
      background: color-mix(in srgb, var(--bg-elevated, #161920) 88%, transparent);
      color: var(--text-strong, #f4f4f5);
      font-size: 13px;
      font-family: inherit;
      cursor: default;
    }

    .field-input:disabled {
      opacity: 0.72;
    }

    .picker-btn {
      flex: 0 0 auto;
      min-width: 36px;
      padding: 0 10px;
      border-radius: 8px;
      border: 1px solid color-mix(in srgb, var(--border) 76%, transparent);
      background: color-mix(in srgb, var(--bg-elevated, #161920) 88%, transparent);
      color: var(--text, #e4e5e7);
      font-size: 16px;
      line-height: 1;
      cursor: pointer;
    }

    .picker-btn:hover:not(:disabled) {
      border-color: color-mix(in srgb, var(--accent, #6ea8fe) 55%, transparent);
      color: var(--accent, #6ea8fe);
    }

    .picker-btn:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .backdrop {
      position: fixed;
      inset: 0;
      z-index: 1200;
      background: rgba(0, 0, 0, 0.55);
      display: grid;
      place-items: center;
      padding: 20px;
      box-sizing: border-box;
    }

    .modal {
      width: min(420px, calc(100vw - 36px));
      max-height: calc(100vh - 40px);
      overflow: hidden;
      background: var(--bg, #161920);
      border: 1px solid var(--border, #2e2f33);
      border-radius: 12px;
      box-shadow: 0 22px 48px rgba(0, 0, 0, 0.48);
      display: flex;
      flex-direction: column;
    }

    .modal-header {
      padding: 14px 18px;
      border-bottom: 1px solid color-mix(in srgb, var(--border) 75%, transparent);
      background: color-mix(in srgb, var(--bg-elevated, #1b1f2a) 90%, transparent);
    }

    .modal-header h3 {
      margin: 0;
      font-size: 16px;
      line-height: 1.25;
      color: var(--text-strong, #f4f4f5);
    }

    .modal-body {
      padding: 12px 18px;
      overflow: auto;
      max-height: min(420px, calc(100vh - 180px));
    }

    .option {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 8px;
      border-radius: 8px;
      cursor: pointer;
      color: var(--text, #e4e5e7);
      font-size: 14px;
    }

    .option:hover {
      background: color-mix(in srgb, var(--bg-hover, #2a2b30) 80%, transparent);
    }

    .option input {
      margin: 0;
      accent-color: var(--accent, #6ea8fe);
    }

    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      padding: 12px 18px 16px;
      border-top: 1px solid color-mix(in srgb, var(--border) 75%, transparent);
    }

    .btn {
      border-radius: 8px;
      padding: 8px 14px;
      font-size: 13px;
      cursor: pointer;
      border: 1px solid color-mix(in srgb, var(--border) 76%, transparent);
      background: transparent;
      color: var(--text, #e4e5e7);
    }

    .btn:hover:not(:disabled) {
      background: color-mix(in srgb, var(--bg-hover, #2a2b30) 80%, transparent);
    }

    .btn:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .btn-primary {
      border-color: color-mix(in srgb, var(--accent, #6ea8fe) 55%, transparent);
      background: color-mix(in srgb, var(--accent, #6ea8fe) 18%, transparent);
      color: var(--text-strong, #f4f4f5);
    }
  `;

  @property({ type: String }) value: AiAnswerLanguage = "en";
  @property({ type: String }) locale: UiLocale = "en";
  /** When false, show readonly label only (no … picker). */
  @property({ type: Boolean }) pickable = true;
  @property({ type: Boolean }) disabled = false;

  @state() private _modalOpen = false;
  @state() private _draft: AiAnswerLanguage = "en";

  private _t(key: string): string {
    return t(key, undefined, this.locale);
  }

  private _label(code: AiAnswerLanguage): string {
    return answerLanguageLabel(code, (key) => this._t(key));
  }

  private _resolvedValue(): AiAnswerLanguage {
    return normalizeAnswerLanguage(this.value, defaultAnswerLanguage(this.locale));
  }

  private _openModal(): void {
    if (!this.pickable || this.disabled) return;
    this._draft = this._resolvedValue();
    this._modalOpen = true;
  }

  private _closeModal(): void {
    this._modalOpen = false;
  }

  private _apply(): void {
    const next = this._draft;
    this._closeModal();
    if (next === this._resolvedValue()) return;
    this.dispatchEvent(
      new CustomEvent("language-change", {
        detail: { value: next },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _onModalKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      event.preventDefault();
      this._closeModal();
    }
  }

  render() {
    const current = this._resolvedValue();
    return html`
      <div class="field-row">
        <input
          class="field-input"
          type="text"
          readonly
          .value=${this._label(current)}
          ?disabled=${this.disabled}
          aria-readonly="true"
        />
        ${this.pickable
          ? html`
              <button
                class="picker-btn"
                type="button"
                title=${this._t("projects.answerLanguagePickerBrowse")}
                aria-label=${this._t("projects.answerLanguagePickerBrowse")}
                ?disabled=${this.disabled}
                @click=${this._openModal}
              >
                …
              </button>
            `
          : ""}
      </div>
      ${this._modalOpen
        ? html`
            <div
              class="backdrop"
              @click=${(event: MouseEvent) => {
                if (event.target === event.currentTarget) this._closeModal();
              }}
            >
              <div
                class="modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="ai-answer-language-title"
                @keydown=${this._onModalKeydown}
              >
                <div class="modal-header">
                  <h3 id="ai-answer-language-title">
                    ${this._t("projects.answerLanguagePickerTitle")}
                  </h3>
                </div>
                <div class="modal-body">
                  ${AI_ANSWER_LANGUAGE_OPTIONS.map(
                    (code) => html`
                      <label class="option">
                        <input
                          type="radio"
                          name="ai-answer-language"
                          .value=${code}
                          .checked=${this._draft === code}
                          @change=${() => {
                            this._draft = code;
                          }}
                        />
                        <span>${this._label(code)}</span>
                      </label>
                    `,
                  )}
                </div>
                <div class="modal-footer">
                  <button class="btn" type="button" @click=${this._closeModal}>
                    ${this._t("projects.cancel")}
                  </button>
                  <button class="btn btn-primary" type="button" @click=${this._apply}>
                    ${this._t("projects.answerLanguagePickerApply")}
                  </button>
                </div>
              </div>
            </div>
          `
        : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-ai-answer-language-field": EadAiAnswerLanguageField;
  }
}
